/**
 * harness/useKeyboardShortcuts.js
 * T-Cron-2026-10-22 14:00 (round-2-of-day) — 改进清单 #4「快捷键」
 *
 * 为什么需要:Vite 树里**一个全局快捷键都没有**。只有 PostCard(卡片内)
 * 和 PostDetail / MobileFilterDrawer(各自 Esc)挂了 keydown。
 * 用户在根目录老仓库里已经习惯了 `/` 搜索、`1/2` 切视图、`c` 复制、
 * `f` 收藏;迁到 Vite 树后这套肌肉记忆全部失效,而且**没有任何地方告诉
 * 用户新快捷键是什么**(根目录老仓库有 SHORTCUT_GROUPS 帮助面板,Vite 树没有)。
 *
 * 设计约束(逐条对应"不破坏 React 渲染"):
 *   1. **只在焦点不在输入框时生效**。用户正在搜索框里打字时按 `1`
 *      必须是输入数字 1,不能切视图。
 *   2. **不拦截浏览器/系统级组合键**。Ctrl/Cmd/Alt + 任何键直接放行,
 *      否则会吃掉 Ctrl+R(刷新)、Ctrl+F(查找)、Cmd+L(地址栏)等。
 *   3. **事件监听挂在 document 上,而不是组件树里**,避免每个卡片都挂一份
 *      监听器(428 张卡片 = 428 个 listener,滚动会卡)。单例 + useEffect
 *      清理,热路径上零额外开销。
 *   4. **数字键 1/2 与 PostCard 内的版本数字键冲突**:卡片自己 stopPropagation
 *      了(PostCard.jsx onKeyDown),所以卡片聚焦时按 1 只会切版本,不会切视图。
 *      本 hook 只在**焦点不在卡片上**时接管 1/2 —— 与老仓库 App.jsx 的
 *      语义一致(老仓库是"卡片聚焦时 1/2/3 归卡片,否则全局切视图")。
 *
 * 快捷键契约:
 *   /   → 聚焦搜索框(桌面端侧栏;移动端打开筛选抽屉并聚焦搜索)
 *   1   → 网格视图
 *   2   → 列表视图
 *   e   → 导出当前可见(CSV)
 *   m   → 进入 / 退出多选模式(批量复制 / 收藏)
 *   c   → 复制当前详情文案(仅详情抽屉打开时)
 *   f   → 收藏/取消收藏当前详情(仅详情抽屉打开时)
 *   t   → 回到顶部(与右下角 FAB 等效)
 *   Shift+F → 跳到最新批次(与 Header 徽标等效)
 *   Shift+R → 重置全部筛选(与 Sidebar「全清」等效)
 *   a   → 多选模式下全选 / 取消全选(仅 selectMode)
 *   ?   → 打开快捷键帮助面板
 *   Esc → 关闭帮助面板
 *
 * ⚠️ Shift 组合键的处理顺序(本轮新增,踩过坑):
 *   `e.key` 在按住 Shift 时是**大写字母**('R' / 'F'),不是小写。
 *   所以判断必须用 `e.key === 'R' && e.shiftKey`,而不是 `e.key === 'r'`。
 *   同时上面的组合键放行条件只挡 ctrl/meta/alt —— **不能挡 shift**,
 *   否则 Shift+F / Shift+R 永远到不了 switch。
 *   ⚠️ 还有一处顺序陷阱:纯小写 `f` 和 `c` 的分支在 switch 里,
 *   而 Shift+F 的 `e.key` 是 'F'(大写)⇒ 两者天然不冲突,
 *   但 Shift+R 的 'R' 与小写 'r' 也不冲突 —— 靠的是大小写区分,不是 if 顺序。
 *
 * 注意:本文件用 React,所以**必须显式 import React**(vite.config.js 的
 * esbuild.jsxInject 只对 .jsx 生效,.js 文件拿不到自动注入 —— 这是
 * 08:00 轮踩过的坑,见 references/authoritative-resync-before-deploy.md)。
 */
import React from 'react';

/**
 * 判断当前焦点是否落在可输入元素上。
 * 覆盖: input / textarea / select / contenteditable / role=textbox
 */
function isEditableTarget(target) {
  if (!target) return false;
  const tag = (target.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (target.isContentEditable) return true;
  const role = target.getAttribute && target.getAttribute('role');
  if (role === 'textbox' || role === 'searchbox' || role === 'combobox') return true;
  return false;
}

/**
 * 焦点是否在某个卡片内(PostCard 有 role="button" + tabIndex=0)。
 * 卡片聚焦时,数字键归卡片(切版本),不归全局(切视图)。
 */
function isInsidePostCard(target) {
  return !!(target && target.closest && target.closest('.post-card'));
}

/**
 * @param {Object} opts
 * @param {() => void} opts.onSearch   聚焦搜索
 * @param {() => void} opts.onViewGrid 切网格视图
 * @param {() => void} opts.onViewList 切列表视图
 * @param {() => void} [opts.onExport] 导出当前可见
 * @param {() => void} [opts.onToggleSelectMode] 进入/退出多选模式
 * @param {() => void} [opts.onCopy]   复制(详情打开时)
 * @param {() => void} [opts.onCopyAllVersions] 复制 A/B/C 三版(详情打开时,Shift+C)
 * @param {() => void} [opts.onFav]    收藏(详情打开时)
 * @param {() => void} [opts.onHelp]   打开帮助面板
 * @param {() => boolean} [opts.isDetailOpen] 详情抽屉是否打开
 * @param {() => void} [opts.onBackToTop]   回到顶部(快捷键 t)
 * @param {() => void} [opts.onJumpLatest]   跳到最新批次(快捷键 Shift+F)
 * @param {() => void} [opts.onResetFilters] 重置全部筛选(快捷键 Shift+R)
 * @param {() => void} [opts.onSelectAll]    多选模式全选/取消全选(快捷键 a)
 * @param {() => boolean} [opts.isSelectMode] 当前是否处于多选模式(快捷键 a 的前置条件)
 * @param {() => boolean} [opts.isModalOpen] 是否有模态弹层打开(帮助面板/详情抽屉/筛选抽屉/导出菜单)
 * @param {boolean} [opts.helpOpen] 帮助面板是否打开(用于区分放行策略)
 * @param {boolean} [opts.filterOpen] 移动端筛选抽屉是否打开(用于区分放行策略)
 * @param {boolean} [opts.exportMenuOpen] Header 导出菜单是否打开(用于区分放行策略)
 */
function useKeyboardShortcuts(opts) {
  const optsRef = React.useRef(opts);
  // 把最新 opts 存进 ref,让 keydown 监听器始终调用最新回调,
  // 避免因为依赖数组变化而反复 remove/add listener。
  //
  // T-Cron-2026-10-22 14:00:直接在 **render 期**赋值(而不是 useEffect)。
  // 用 useEffect 会留下一个 stale 窗口:render 之后、effect 执行之前
  // 到达的按键事件会拿到上一帧的 opts(比如 detailId 已经是新的、
  // isDetailOpen 却还返回 false)。render 期赋值没有这个窗口。
  optsRef.current = opts;

  React.useEffect(() => {
    const handleKey = (e) => {
      const o = optsRef.current || {};

      // 【放行哪些】组合键(Ctrl/Cmd/Alt)整体放行 —— 它们的 repeat 是
      //   浏览器/系统自己的语义(如 Alt+↓ 连续重排侧栏是**期望行为**),
      //   所以 repeat 判断放在组合键判断**之后**。
      //
      // 1) 组合键一律放行(刷新/查找/地址栏/系统快捷键)
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // 🔴 T137 (2026-11-03 08:00 早间) — 长按(auto-repeat)守卫。
      //
      // 【为什么必须挡】浏览器按住一个键不放会**持续重复**派发 keydown
      //   (e.repeat === true,典型 30ms 一次)。本文件里的键位有大量
      //   **toggle 型**与**副作用型**动作,重复触发后果严重:
      //     m → toggleSelectMode  反复切换 ⇒ 批量操作条闪烁,松手后状态随机
      //     a → toggleSelectAll   反复全选/取消 ⇒ 「全选 459 张」闪成不闪
      //     ? → setHelpOpen(v=>!v)反复开合 ⇒ 帮助面板闪
      //     e → exportVisible('csv') 连续弹 5~10 次 CSV 下载确认
      //     1/2 → setViewMode 反复重渲染整个网格 ⇒ 列表模式卡顿闪烁
      //   ⇒ 用户「按住不放」= 误触发一堆不可撤销的副作用。
      //
      // 【为什么不逐键挡】逐键加守卫 = 以后新增键位必然又漏
      //   ⇒ 在**入口**统一挡一次,覆盖全部 case。
      if (e.repeat) return;

      // 2) 输入态下,只放行 Escape,其他单键全部让给输入法/输入框
      if (isEditableTarget(e.target)) return;

      const key = e.key;

      // 3) Esc:关帮助面板
      if (key === 'Escape') {
        if (o.onHelp && o.helpOpen) {
          e.preventDefault();
          o.onHelp();
        }
        return;
      }

      // 4) 卡片内的数字键归卡片(切版本),全局不抢
      if (/^[0-9]$/.test(key) && isInsidePostCard(e.target)) return;

      // 4.5) 🔴 T136 (2026-11-03 02:00):模态弹层打开时,全局单键一律让位。
      //
      // 【为什么必须在这里统一挡,而不是逐键加守卫】
      //   `aria-modal="true"` 只约束读屏(AT)和 Tab 焦点,**不约束键盘事件**。
      //   ⇒ 帮助面板(aria-modal)开着时按 2 → 背景看板被切成列表视图;
      //     详情抽屉(aria-modal)开着时按 m → 背景进入多选态。
      //   之前只有 1/2 两条数字键有 isDetailOpen 守卫(那是 T1xx 打的单点补丁),
      //   e/m/t/a/C/f 全都漏网 ⇒ 「模态」只对一半键有效。
      //
      // 【实测复现(线上 canonical,2026-11-02)】
      //   帮助面板开 → 按 2 → .post-grid class 变 "post-grid list-mode"(背景被切了)
      //   抽屉开     → 按 m → BatchActionBar 从不存在变存在
      //   抽屉开     → 按 t → .app-main.scrollTop 3000 → 78(背景被滚回顶部)
      //
      // 【为什么不能简单"模态开就全挡"】
      //   详情抽屉自己要用 1..9 / ←→ / d / s 切版本和下载 —— 那些在
      //   PostDetail 的 window-capture handler 里(带 stopPropagation),
      //   本文件**没有**这些 case ⇒ 这里挡掉不会影响抽屉自有键。
      //   但 c / C / f 是**全局**实现(复制/复制三版/收藏当前版),
      //   抽屉打开时它们是抽屉上下文的合法操作 ⇒ 必须放行。
      //
      // 【判据】模态栈顶只有一层 ⇒ 用 helpOpen 精确区分帮助面板与详情抽屉,
      //   而不是笼统地"有模态就全挡"(那会把抽屉里的 c/f 也打死)。
      if (o.isModalOpen && o.isModalOpen()) {
        if (o.helpOpen) {
          // 帮助面板:只放行 ?(关闭)和 Escape(关闭)。
          // 面板 footer 明确承诺「按 ? 或 Esc 关闭」(App.jsx)⇒ ? 必须能穿透。
          if (key !== '?' && key !== 'Escape') return;
        } else if (o.filterOpen) {
          // 🔴 T137 (2026-11-03 08:00 早间):移动端筛选抽屉(第 3 种模态)。
          //
          // 【为什么 T136 把它漏在外面】T136 的守卫只含 helpOpen / detailId,
          //   当时写的理由是「它是移动端唯一的筛选入口,挡掉背景单键反而更安全」
          //   —— 但那句话把两件事混在一起了:
          //     「抽屉**内部**要能继续点筛选项」 vs 「抽屉打开时**背景**的单键要能触发」
          //   这两件事**不冲突**:抽屉内部的输入框已被 isEditableTarget 让位
          //   (useKeyboardShortcuts.js:133),背景单键则应该被挡住。
          //
          // 【实测症状】移动端筛选抽屉开着时按 m ⇒ 背景看板进入多选态,
          //   BatchActionBar(z=60)出现在抽屉(z=101)**背后** ⇒ 100% 点不到
          //   (与 T136 修掉的「详情抽屉里按 m」是同一族假入口)。
          //   按 e ⇒ 抽屉开着仍然导出 CSV(用户以为在筛选,文件却下载了)。
          //   按 t ⇒ 背景被滚回顶部(筛选位置丢失)。
          //
          // 【放行哪些】Escape(关抽屉)/ Enter(提交搜索,输入框焦点时
          //   已被 isEditableTarget 让位)/ 其余全挡。
          if (key !== 'Escape' && key !== 'Enter') return;
        } else if (o.exportMenuOpen) {
          // 🔴 T138 (2026-11-03 03:30 早间):Header 的导出菜单(第 4 种模态)。
          //
          // 【它不是 aria-modal,但是模态】role="menu" + aria-haspopup="menu"
          //   的弹出菜单:焦点锁在菜单项之间(↑/↓ 导航),Esc 关闭并归还焦点。
          //   ⇒ 背景单键必须让位。
          //
          // 【实测症状(pre-fix,本地运行时探针)】
          //   菜单开着按 `2` → 背景 .post-grid 'post-grid ' → 'post-grid list-mode'
          //   菜单开着按 `e` → downloads=2(**导出两次**:菜单 item + 全局 e)
          //   菜单开着按 `m` → batch_bar=True(背景进入多选态)
          //
          // 【为什么箭头键要放行】菜单自己的 onKeyDown(React 合成事件,
          //   在 root 上冒泡)处理 ↑/↓/Enter/Space。它**先于**本引擎的
          //   document 冒泡 listener 跑 ⇒ 菜单已经处理完焦点移动。
          //   但本引擎若**不** return,↑/↓ 之后全局的「切版本/切视图」类
          //   逻辑还会再跑一遍 ⇒ 双重响应。所以这里必须挡。
          //
          // 【放行哪些】Escape(Header 的 document listener 会关菜单)/ 其余全挡。
          //   ⚠️ Enter **不能**放行 —— 菜单的 Enter 是「导出所选项」,
          //     全局的 e/Enter 是「导出 CSV」⇒ 放行会导出两次(实测 downloads=2)。
          if (key !== 'Escape') return;
        } else {
          // 详情抽屉:放行 ? / Escape / c / C / f(抽屉上下文的复制与收藏)。
          if (key !== '?' && key !== 'Escape' && key !== 'c' && key !== 'C' && key !== 'f') return;
        }
      }

      switch (key) {
        case '/': {
          if (!o.onSearch) return;
          e.preventDefault();
          o.onSearch();
          return;
        }
        case '1': {
          // T-Cron-2026-10-28 03:30 (T118) — 改进清单 #2「快捷键」补齐。
          //
          // 🔴 修一个**真实的手感 bug**:详情抽屉打开时按 `1` / `2`,
          //    用户以为是在切 A/B 版,实际把**抽屉背后的看板**切成了网格/列表
          //    —— 抽屉里的版本没动,背后的视图却闪了一下,滚动位置也丢了。
          //
          // 根因:全局 handler 的 `1`/`2` 分支**没有 isDetailOpen 守卫**,
          // 而 `c` / `f` / `C` 都有(见下面)。数字键在详情里是「切版本」
          // (PostCard 的 onKeyDown 也是这个约定),所以这里必须让位。
          //
          // 为什么不能靠 PostDetail 的 stopPropagation 解决:
          //   全局 listener 挂在 `document`(冒泡阶段),PostDetail 的
          //   document listener 也挂在 document 上 —— 同阶段时
          //   **先注册的先执行**,谁先注册取决于挂载顺序,不可靠。
          //   在源头(global handler)让位是唯一确定的做法。
          if (o.isDetailOpen && o.isDetailOpen()) return;
          if (!o.onViewGrid) return;
          e.preventDefault();
          o.onViewGrid();
          return;
        }
        case '2': {
          // 同上:`2` 在详情打开时归「切 B 版」,不归「切列表视图」。
          if (o.isDetailOpen && o.isDetailOpen()) return;
          if (!o.onViewList) return;
          e.preventDefault();
          o.onViewList();
          return;
        }
        case 'e': {
          // T-Cron-2026-10-23 03:30:导出当前可见(CSV)。
          // 老仓库有这条(App.jsx:1088-1100),Vite 迁移时丢了。
          // 与老仓库同约定:单键 e,Ctrl/Cmd/Alt+e 已被上面的组合键放行。
          if (!o.onExport) return;
          e.preventDefault();
          o.onExport();
          return;
        }
        case 'm': {
          // T-Cron-2026-10-24 03:30:进入 / 退出多选模式(批量复制 / 收藏)。
          // 与老仓库同约定:单键 m,Ctrl/Cmd/Alt+m 已被上面的组合键放行。
          if (!o.onToggleSelectMode) return;
          e.preventDefault();
          o.onToggleSelectMode();
          return;
        }
        case 'c': {
          // 只有详情抽屉打开时才有"当前文案"可复制
          if (!o.onCopy || !o.isDetailOpen || !o.isDetailOpen()) return;
          e.preventDefault();
          o.onCopy();
          return;
        }
        case 'C': {
          // Shift+C = 复制 A/B/C 三版(老仓库 PostDetail 的 handleCopyAll 同约定)。
          // ⚠️ e.key 在 Shift 按住时是大写 'C' —— 所以这里只可能由 Shift+C
          // 进入,普通 'c' 归上面的「复制当前版」分支。
          if (!e.shiftKey || !o.onCopyAllVersions || !o.isDetailOpen || !o.isDetailOpen()) return;
          e.preventDefault();
          o.onCopyAllVersions();
          return;
        }
        case 'f': {
          if (!o.onFav || !o.isDetailOpen || !o.isDetailOpen()) return;
          e.preventDefault();
          o.onFav();
          return;
        }
        case 't': {
          // T-Cron-2026-10-25 08:00 早间 — 改进清单 #4「快捷键」补齐。
          // 与右下角 FAB(回到顶部)同一个动作:滚动容器是 .app-main,
          // 不是 window —— 所以由 App.jsx 的 handler 负责滚动哪个容器。
          if (!o.onBackToTop) return;
          e.preventDefault();
          o.onBackToTop();
          return;
        }
        case 'a': {
          // 多选模式全选 / 取消全选(与老仓库 App.jsx 的 `a` 同约定)。
          // 只在 selectMode 下生效 —— 否则这个键会被浪费掉,而且
          // 正常浏览时误按 'a' 选中 459 张卡片是灾难性的。
          if (!o.onSelectAll || !o.isSelectMode || !o.isSelectMode()) return;
          e.preventDefault();
          o.onSelectAll();
          return;
        }
        case 'R': {
          // Shift+R = 重置全部筛选。
          // ⚠️ e.key 在 Shift 按住时是大写 'R' —— 所以这里只可能由
          // Shift+R 进入,普通 'r' 会落到 default 分支。
          if (!e.shiftKey || !o.onResetFilters) return;
          e.preventDefault();
          o.onResetFilters();
          return;
        }
        case 'F': {
          // Shift+F = 跳到最新批次(与 Header 徽标等效)。
          // 同上:e.key 是大写 'F',小写 'f' 归上面的收藏分支。
          if (!e.shiftKey || !o.onJumpLatest) return;
          e.preventDefault();
          o.onJumpLatest();
          return;
        }
        case '?': {
          if (!o.onHelp) return;
          e.preventDefault();
          o.onHelp();
          return;
        }
        default:
          return;
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);
}

window.useKeyboardShortcuts = useKeyboardShortcuts;

export default useKeyboardShortcuts;
